import { defineStore } from "pinia";
import { ShopZone } from "@src/api";
import { normalize } from "@src/utils";

export type ShopZoneExtended = ShopZone & {
  parent_id?: string;
  children?: ShopZoneExtended[];
};

type State = {
  ids: string[];
  map: Record<string, ShopZoneExtended>;
};

const state_default: State = {
  ids: [],
  map: {},
};

/**
 * рекурсивно собирает все zone_id потомков (children, их children и т.д.) по дереву зон в map.
 * @param map - зоны
 * @param zone_id - id удаляемой зоны
 * @returns - массив id's
 */
function collectDescendantIds(map: Record<string, ShopZoneExtended>, zone_id: string): string[] {
  const zone = map[zone_id];
  const ids: string[] = [];

  if (zone?.children?.length) {
    for (const child of zone.children) {
      ids.push(child.zone_id);
      ids.push(...collectDescendantIds(map, child.zone_id));
    }
  }

  return ids;
}

export const useShopZoneListStore = defineStore("shop_zone_list", {
  state: () => state_default,
  actions: {
    add(shop_zone_list: ShopZoneExtended[]) {
      const normalized = normalize<ShopZone, "zone_id">(shop_zone_list, "zone_id");
      const noop = { zone_id: "noop", children: [] };

      // Добавляем в каждую зону родительскую и дочерние зоны, если есть
      for (let i = 0; i < shop_zone_list.length; i++) {
        const current: ShopZoneExtended = normalized.map[shop_zone_list[i].zone_id];

        // Если зона неактивна, то не проращиваем её в общую иерархию
        if (current.status === "inactive") continue;

        const hierarchy = current.hierarchy.split(".");
        const prev: ShopZoneExtended = normalized.map[hierarchy.at(-2)];

        if (prev) {
          current.parent_id = prev.zone_id;
        } else {
          current.parent_id = "noop";
          noop.children.push(current);

          continue;
        }

        if (prev.children) {
          prev.children.push(current);
        } else {
          prev.children = [current];
        }
      }

      this.map = { ...this.map, ...normalized.map, [noop.zone_id]: noop };
      this.ids = [noop.zone_id, ...normalized.ids];
    },
    addChild(parent_zone_id: string, new_zone: ShopZone) {
      const parent = this.map[parent_zone_id];

      if (!parent) return;

      const zone: ShopZoneExtended = { ...new_zone, parent_id: parent_zone_id };
      const children = parent.children ? [...parent.children, zone] : [zone];

      Object.assign(parent, { children });

      this.map[zone.zone_id] = zone;
      this.ids.push(zone.zone_id);
    },
    update(shop_zone_update: ShopZoneExtended) {
      const shop_zone = this.map[shop_zone_update.zone_id];

      if (shop_zone) {
        Object.assign(shop_zone, shop_zone_update);
      }
    },
    delete(shop_zone_id: string) {
      const shop_zone = this.map[shop_zone_id];
      if (!shop_zone) return;

      const ids_to_delete = [shop_zone_id, ...collectDescendantIds(this.map, shop_zone_id)];

      if (shop_zone.parent_id) {
        const parent = this.map[shop_zone.parent_id];

        if (parent?.children) {
          Object.assign(parent, { children: parent.children.filter((z) => z.zone_id !== shop_zone_id) });
        }
      }

      this.ids = this.ids.filter((id) => !ids_to_delete.includes(id));

      for (const id of ids_to_delete) {
        delete this.map[id];
      }
    },
    has(userId: string) {
      return this.map[userId] !== undefined;
    },
  },
  getters: {
    list_ids_without_parent: (state) => {
      return state.ids.filter((id) => !state.map[id].parent_id);
    },
    is_empty: (state) => {
      return state.ids.length === 0;
    },
  },
});
